import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Plus } from 'lucide-react';
import FloatingButton from '../../../components/common/FloatingButton';
import TestHistoryItem from './TestHistoryItem';
import KyTestModal from '../../../components/StudentManagement/KyTestModal';

const TestHistoryPanel = ({
  testHistory,
  selectedTest,
  isTestHistoryLoading,
  hasMoreTests,
  onTestSelect,
  testHistoryRef,
  isMobile = false,
  setCurrentMobilePanel,
  // 인피니티 스크롤을 위한 추가 props
  onLoadMore,
  // 학생 정보 추가
  selectedStudent,
  // 컴팩트 모드(학습 /students-hub 세로형 패널): 카드 높이 축소
  compact = false
}) => {
  const [showKyTestModal, setShowKyTestModal] = useState(false);
  
  // 각 테스트 아이템의 ref를 저장하기 위한 ref 맵
  const testItemRefs = useRef({});
  
  // 이전 selectedTest의 exam_id를 추적하여 실제 테스트 변경 감지
  const prevSelectedTestId = useRef(null);
  
  // 테스트 목록이 변경될 때 불필요한 ref 정리
  useEffect(() => {
    const currentExamIds = new Set(testHistory.map(test => test.exam_id));
    const refsKeys = Object.keys(testItemRefs.current);
    
    refsKeys.forEach(examId => {
      if (!currentExamIds.has(parseInt(examId))) {
        delete testItemRefs.current[examId];
      }
    });
  }, [testHistory]);
  
  const handleTestClick = (test) => {
    onTestSelect(test);
    if (isMobile && setCurrentMobilePanel) {
      setCurrentMobilePanel('area3');
    }
  };
  
  // 선택된 테스트가 변경될 때 해당 테스트로 자동 스크롤
  useEffect(() => {
    if (selectedTest && testHistoryRef?.current) {
      const currentTestId = selectedTest.exam_id;
      const isTestActuallyChanged = prevSelectedTestId.current !== currentTestId;
      
      console.log('🎯 자동 스크롤 검토:', {
        exam_id: currentTestId,
        prevTestId: prevSelectedTestId.current,
        isTestActuallyChanged,
        testHistoryLength: testHistory.length,
        hasRef: !!testItemRefs.current[currentTestId]
      });
      
      // 실제로 테스트가 변경된 경우에만 스크롤 실행
      if (isTestActuallyChanged) {
        console.log('✅ 테스트 변경 감지 - 자동 스크롤 시도:', currentTestId);
        
        // DOM 업데이트를 기다린 후 스크롤 실행
        const scrollToSelectedTest = (retryCount = 0) => {
          const selectedTestRef = testItemRefs.current[currentTestId];
          const maxRetries = 5; // 최대 5번 재시도
          
          if (selectedTestRef) {
            console.log('✅ 선택된 테스트로 자동 스크롤 실행:', currentTestId);
            
            // 부드럽게 스크롤하되, 가운데 정렬
            selectedTestRef.scrollIntoView({
              behavior: 'smooth',
              block: 'center',  // 가운데 정렬
              inline: 'nearest'
            });
          } else if (retryCount < maxRetries) {
            console.log(`⏳ 테스트 ref 준비 중... (${retryCount + 1}/${maxRetries})`, currentTestId);
            // 점진적으로 지연 시간 증가 (50ms, 100ms, 200ms, 400ms, 800ms)
            const delay = Math.min(50 * Math.pow(2, retryCount), 800);
            setTimeout(() => scrollToSelectedTest(retryCount + 1), delay);
          } else {
            console.warn('❌ 테스트 ref를 찾지 못함 (최대 재시도 초과):', currentTestId);
          }
        };
        
        // URL 직접 접근인지 확인 (URL에 testId가 있는 경우)
        const isDirectUrlAccess = window.location.pathname.includes('/test/');
        const initialDelay = isDirectUrlAccess ? 200 : 50; // URL 직접 접근 시 더 긴 지연
        
        setTimeout(scrollToSelectedTest, initialDelay);
        
        // 이전 테스트 ID 업데이트
        prevSelectedTestId.current = currentTestId;
      } else {
        console.log('ℹ️ 동일한 테스트 - 인피니티 스크롤로 인한 호출, 스크롤 스킵');
      }
    } else if (selectedTest) {
      // selectedTest는 있지만 testHistoryRef가 없는 경우, prevSelectedTestId만 업데이트
      prevSelectedTestId.current = selectedTest.exam_id;
    }
  }, [selectedTest?.exam_id, testHistory.length]); // 의존성은 유지하되 조건부로 스크롤 실행

  // 인피니티 스크롤 핸들러
  const handleScroll = useCallback((e) => {
    if (!onLoadMore || isTestHistoryLoading || !hasMoreTests) return;
    
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    
    // 스크롤이 하단 100px 이내에 도달하면 다음 페이지 로드
    const threshold = 100;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    
    if (isNearBottom) {
      console.log('TestHistoryPanel: Near bottom detected, triggering load more...');
      onLoadMore();
    }
  }, [onLoadMore, isTestHistoryLoading, hasMoreTests]);

  if (isMobile) {
    // 모바일 뷰
    return (
      <div 
        className="flex min-h-0 flex-1 flex-col relative"
        style={{ width: 'calc(100% - 28.8px)', margin: '7.2px auto 14.4px auto' }}
      >
        <div className="border-b bg-white p-3 rounded-t-3xl">
          <h4 className="text-[14px] font-semibold">테스트 목록</h4>
        </div>
        
        <div 
          ref={testHistoryRef}
          className="flex-1 space-y-2 overflow-y-auto bg-gray-50 p-3 rounded-b-3xl shadow-lg"
          onScroll={handleScroll}
        >
          {testHistory.length > 0 ? (
            <>
              {testHistory.map((test, index) => (
                <TestHistoryItem
                  key={`${test.stamp_id}-${test.exam_id}-${index}`}
                  ref={(el) => {
                    if (el) {
                      testItemRefs.current[test.exam_id] = el;
                    }
                  }}
                  test={test}
                  index={index}
                  isSelected={selectedTest?.exam_id === test.exam_id}
                  onClick={() => handleTestClick(test)}
                />
              ))}
              
              {/* 로딩 상태 */}
              {isTestHistoryLoading && (
                <div className="flex items-center justify-center py-4">
                  <p className="text-xs text-gray-500">테스트 목록을 불러오는 중...</p>
                </div>
              )}
              
              {/* 더 이상 데이터가 없을 때 */}
              {!hasMoreTests && !isTestHistoryLoading && testHistory.length > 0 && (
                <div className="flex items-center justify-center py-4">
                  <p className="text-xs text-gray-500">모든 테스트 목록을 불러왔습니다.</p>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center py-8">
              {isTestHistoryLoading ? (
                <p className="text-sm text-gray-500">테스트 목록을 불러오는 중...</p>
              ) : (
                <p className="text-sm text-gray-500">테스트 목록이 없습니다.</p>
              )}
            </div>
          )}
        </div>
        
        {/* 플로팅 버튼 - 모바일 (전역 공용 위치/포털과 동일하게) */}
        {typeof document !== 'undefined' && (
          <FloatingButton
            variant="plus"
            onClick={() => setShowKyTestModal(true)}
            title="새 테스트 추가"
            usePortal={true}
          />
        )}
        
        {/* KyTestModal */}
        {showKyTestModal && selectedStudent && (
          <KyTestModal
            isOpen={showKyTestModal}
            onClose={() => setShowKyTestModal(false)}
            student={selectedStudent}
          />
        )}
      </div>
    );
  }

  // 데스크톱 뷰
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl bg-white shadow-lg relative">
      <div className={`border-b ${compact ? 'p-2.5' : 'p-4'}`}>
        <h3 className={`font-semibold ${compact ? 'text-xs' : 'text-[14px]'}`}>테스트 목록</h3>
      </div>

      <div
        ref={testHistoryRef}
        className={`flex-1 overflow-y-auto bg-gray-50 shadow-lg ${compact ? 'space-y-1.5 p-2' : 'space-y-2 p-3'}`}
        onScroll={handleScroll}
      >
        {testHistory.length > 0 ? (
          <>
            {testHistory.map((test, index) => (
              <TestHistoryItem 
                key={`${test.stamp_id}-${test.exam_id}-${index}`}
                ref={(el) => {
                  if (el) {
                    testItemRefs.current[test.exam_id] = el;
                  }
                }}
                test={test}
                index={index}
                isSelected={selectedTest && selectedTest.exam_id === test.exam_id}
                onClick={() => onTestSelect(test)}
                compact={compact}
              />
            ))}
            
            {/* 로딩 인디케이터 */}
            {isTestHistoryLoading && (
              <div className="flex items-center justify-center py-4">
                <p className="text-xs text-gray-500">테스트 목록을 불러오는 중...</p>
              </div>
            )}
            
            {/* 모든 데이터 로드 완료 메시지 */}
            {!hasMoreTests && testHistory.length > 0 && !isTestHistoryLoading && (
              <div className="flex items-center justify-center py-4">
                <p className="text-xs text-gray-500">모든 테스트 목록을 불러왔습니다.</p>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center py-8">
            {isTestHistoryLoading ? (
              <p className="text-sm text-gray-500">테스트 목록을 불러오는 중...</p>
            ) : (
              <p className="text-sm text-gray-500">테스트 목록이 없습니다.</p>
            )}
          </div>
        )}
      </div>
      
      {/* 플로팅 버튼 - 데스크톱 (선택 영역 하단에 위치) */}
      <div className="absolute left-1/2 bottom-4 -translate-x-1/2 z-10">
        <button
          onClick={() => setShowKyTestModal(true)}
          className="rounded-full shadow-lg bg-glass-white backdrop-blur-none hover:opacity-95 active:opacity-90"
          style={{
            width: '56px',
            height: '56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            outline: 'none',
          }}
          title="새 테스트 추가"
        >
          <Plus size={24} strokeWidth={3} className="text-primary" />
        </button>
      </div>
      
      {/* KyTestModal */}
      {showKyTestModal && selectedStudent && (
        <KyTestModal
          isOpen={showKyTestModal}
          onClose={() => setShowKyTestModal(false)}
          student={selectedStudent}
        />
      )}
    </div>
  );
};

export default TestHistoryPanel; 